import { isEmpty } from 'lodash';

import {
  ENTIRE_AVAILABLE,
  FILTER_AIRCRAFT_TYPE,
  FILTER_ALL,
  FILTER_DIRECT_SERVICES_ONLY,
  FILTER_HAULING_FREIGHT,
  MODE_AIR,
  MODE_WATER,
} from '@/lib/constants';
import { AircraftAttributes, FilterSelection, RouteBlockInterface, RouteScheduleInterface } from '@/types';

// Checks if a filter value is set and the value does not equal ALL
export const hasFilterValue = (value: string) => {
  return value && value !== FILTER_ALL;
};

/**
 * Filter Route Options
 * Filters Route Options and Route Schedules
 */
export const filterRouteOptions = <T extends RouteBlockInterface | RouteScheduleInterface>(
  options: T[],
  filters: FilterSelection,
): T[] => {
  // Return early if no filters
  if (isEmpty(filters)) return options;

  // Loops through either Route options or Schedules (both must contain Attributes Obj)
  // and removes any that don't match ALL set conditions
  return options.filter((option) => {
    const matches = [];

    // Carriers - ONLY Aircraft & Ships
    if (filters.carriers?.value && hasFilterValue(filters.carriers.value)) {
      if (option.mode === MODE_AIR || option.mode === MODE_WATER) {
        const { primaryCarriers: carriers } = option.attributes || {};

        matches.push(carriers?.some((carrier) => filters.carriers?.value?.includes(carrier)));
      }
    }

    // Cargo - ONLY Aircraft
    if (filters.cargoAircraftOnly && option.mode === MODE_AIR) {
      matches.push(option.attributes?.hasFreightOnlyVehicles === ENTIRE_AVAILABLE);
    }

    // Body Type (NarrowBody, Widebody) - ONLY Aircraft
    if (filters.bodyType && hasFilterValue(filters.bodyType)) {
      if (option.attributes?.aircraft) {
        matches.push(option.attributes.aircraft[filters.bodyType as keyof AircraftAttributes] === ENTIRE_AVAILABLE);
      }
    }

    // Filters by number of Transfers
    if (filters.maxTransfers && hasFilterValue(filters.maxTransfers)) {
      // ONLY Aircraft & Ships
      if (option.mode === MODE_AIR && option.attributes?.transferCount) {
        // Ensure stops are included in Air "direct"
        matches.push(
          option.attributes.transferCount <= Number(filters.maxTransfers) &&
            option.attributes.stopCount <= Number(filters.maxTransfers),
        );
      }
      if (option.mode === MODE_WATER && option.attributes?.transferCount) {
        matches.push(option.attributes.transferCount <= Number(filters.maxTransfers));
      }
    }
    // if any matches were checked, return false if any didn't pass the check
    // or just return true (assumes no filters apply)
    return matches.length ? !matches.includes(false) : true;
  });
};

export const filterStandaloneScheduleOptions = <T extends RouteBlockInterface | RouteScheduleInterface>(
  options: T[],
  filters: FilterSelection,
): T[] => {
  if (isEmpty(filters)) return options;

  // Loops through Schedules and removes any that don't match ALL set conditions
  return options.filter((option) => {
    const matches = [];

    if (!isEmpty(filters.carrierIds)) {
      if (option.mode === MODE_AIR || option.mode === MODE_WATER) {
        const { primaryCarriers: carriers } = option.attributes || {};

        matches.push(carriers?.some((carrier) => filters.carrierIds?.includes(carrier)));
      }
    }

    if (filters[FILTER_DIRECT_SERVICES_ONLY]) {
      if (option.mode === MODE_AIR && option.attributes?.transferCount) {
        // Ensure stops are included in Air "direct"
        matches.push(option.attributes.transferCount === 0 && option.attributes.stopCount === 0);
      }
      if (option.mode === MODE_WATER && option.attributes?.transferCount) {
        matches.push(option.attributes.transferCount === 0);
      }
    }

    if (filters[FILTER_AIRCRAFT_TYPE]) {
      // Body Type (NarrowBody, Widebody) and Cargo
      filters[FILTER_AIRCRAFT_TYPE].forEach((filter) => {
        if (option.attributes?.aircraft) {
          if (filter === FILTER_HAULING_FREIGHT && option.mode === MODE_AIR) {
            matches.push(option.attributes?.hasFreightOnlyVehicles === ENTIRE_AVAILABLE);
          } else {
            matches.push(option.attributes.aircraft[filter as keyof AircraftAttributes] === ENTIRE_AVAILABLE);
          }
        }
      });
    }

    // If any matches were checked, return false if any didn't pass the check
    // or just return true (assumes no filters apply)
    return matches.length ? !matches.includes(false) : true;
  });
};
